import { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Heading,
  Column,
  Loader,
  Spinner,
  Row,
  Drawer,
  Empty,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import { AccessControlModal, AccessSubjectTypes, IAccessControlModalsRef } from '@oliasoft/hierarchy-gui';
import { useTranslation } from 'react-i18next';
import { getPricebookAccessRightsList, setAccessRightsBatch } from 'src/store/entities/access-control/access-control';
import translations from 'src/internationalisation/translation-map.json';
import { getPriceBooks, clearPriceBook } from 'src/store/entities/price-book/price-book';
import { selectPriceBookAdding } from 'src/store/entities/price-book/selectors';
import { selectSubjectId } from 'src/store/entities/authorization/selectors';
import type { TRootState } from 'src/store/store-types';
import { PriceBooksList } from './price-books-list';
import { PriceBookTable } from './price-book-table';

const PriceBook = ({
  isAdding,
  getPriceBooks,
  clearPriceBook,
  setAccessRightsBatch,
  currentUserId,
  getPricebookAccessRightsList,
  isSavingAccessRights,
}: PropsFromRedux) => {
  const { t } = useTranslation();
  const location = useLocation();

  const accessControlChildRef = useRef<IAccessControlModalsRef>(null);

  const [selectedPriceBook, setSelectedPriceBook] = useState<{
    priceBookId: string;
    name: string;
    isPriceBookAccessible: boolean;
  } | null>(null);

  useEffect(() => {
    getPriceBooks();
    getPricebookAccessRightsList(AccessSubjectTypes.User);
    if (location.state) {
      setSelectedPriceBook(location.state as { priceBookId: string; name: string; isPriceBookAccessible: boolean });
    }
    return () => {
      if (typeof clearPriceBook === 'function') {
        clearPriceBook();
      }
    };
  }, []);

  const prevIsSaving = useRef(isSavingAccessRights);

  useEffect(() => {
    if (prevIsSaving.current && !isSavingAccessRights) {
      getPricebookAccessRightsList(AccessSubjectTypes.User);
    }
    prevIsSaving.current = isSavingAccessRights;
  }, [isSavingAccessRights, getPricebookAccessRightsList]);

  const [drawerWidth, setDrawerWidth] = useState(300);
  return (
    <>
      <Column scroll spacing={0}>
        {isAdding && (
          <Loader text={t(translations.fetching)} theme="light" cover>
            <Spinner dark />
          </Loader>
        )}
        <Row spacing={0} height="100%">
          <Drawer width={drawerWidth} closedWidth={50} open button border onResize={(width) => setDrawerWidth(width)}>
            <Column scroll width="250px" borderRight spacing={0}>
              <PriceBooksList
                onClickPriceBook={setSelectedPriceBook}
                selectedPriceBook={selectedPriceBook}
                accessControlChildRef={accessControlChildRef}
                currentUserId={currentUserId}
              />
            </Column>
          </Drawer>
          <Column scroll padding>
            {selectedPriceBook &&
              (selectedPriceBook.isPriceBookAccessible ? (
                <>
                  <Heading top>{selectedPriceBook.name}</Heading>
                  <PriceBookTable priceBookId={selectedPriceBook.priceBookId} />
                </>
              ) : (
                <Empty height="100%" width="100%" text={t(translations.accessControl_noAccessToItem)} />
              ))}
          </Column>
        </Row>
      </Column>
      <AccessControlModal ref={accessControlChildRef} handleEntityAccessRightsUpdated={setAccessRightsBatch} />
    </>
  );
};

const mapStateToProps = ({ entities }: TRootState) => ({
  isAdding: selectPriceBookAdding(entities),
  currentUserId: selectSubjectId(entities),
  isSavingAccessRights: entities?.accessControl.isAdding,
});

const mapDispatchToProps = { getPriceBooks, clearPriceBook, setAccessRightsBatch, getPricebookAccessRightsList };

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(PriceBook));

export { Container as PriceBook };
